import { FormGroup } from '@angular/forms';

import { INPUT_TRANSFORMS_BY_QNAME } from './schema-normalizers.util';

/**
 * Input Transform Utility — rewrite what the user typed into its canonical form.
 *
 * ## What it is for
 *
 * A text field declares `ui.inputTransform: ["stripWhitespace", "uppercase"]` in its
 * sheet JSON, and the value is then corrected AS IT IS TYPED, before any validator
 * judges it. Today only the Auditors Report's "Registration number of audit firm"
 * (`ssmt:RegistrationNumberOfAuditFirm`) uses it, but the mechanism is generic.
 *
 * This is deliberately separate from `input-filter.ts`: that file DELETES characters a
 * field may not hold (`ui.inputFilter`); this one REWRITES characters that are allowed
 * but not canonical (lowercase -> uppercase). A field can use both.
 *
 * ## The three seams (all must keep working)
 *
 * Same shape as `ui.trimLeadingZeros` — see `.claude/rules/filing-form-dropdowns.md`.
 *
 * 1. **Typing / paste** — {@link applyInputTransform} and {@link onPasteInputTransform}.
 * 2. **Inbound state** — `FilingFormStateService.applyValueNormalizersToState` reads
 *    {@link INPUT_TRANSFORMS_BY_QNAME} (derived from the schema) so a value arriving
 *    from the backend as `af 1621` is stored as `AF1621`, in BOTH the original and
 *    modified state (so the edited/pencil flag never trips on a value the user did
 *    not type).
 * 3. **Debounced form write-back** — {@link applyInputTransformsToForm}, called at the
 *    top of the sheet's `persist` callback, so the form control and the state that is
 *    submitted can never disagree.
 *
 * The validator stays STRICT (P.O. decision): it never cleans a value itself. These
 * seams make sure it only ever sees the cleaned one.
 */

/** A transform a schema may request. Applied in the fixed order of {@link TRANSFORM_ORDER}. */
export type InputTransform = 'stripWhitespace' | 'uppercase';

/**
 * Whitespace is removed BEFORE the case change and BEFORE the maxLength cap.
 * Capping first would let pasted padding (`AF 001800`, 9 chars) push real
 * characters past the cap and then be thrown away with the spaces.
 */
const TRANSFORM_ORDER: readonly InputTransform[] = ['stripWhitespace', 'uppercase'];

/**
 * Apply the requested transforms, then cap at `maxLength`.
 *
 * Character-wise and order-preserving — which is what lets the caret be projected
 * by running this over the text before the caret. Idempotent.
 */
export function applyInputTransforms(
  value: string,
  transforms: readonly InputTransform[] | undefined,
  maxLength?: number
): string {
  let next = value;
  for (const transform of TRANSFORM_ORDER) {
    if (!transforms?.includes(transform)) continue;
    next = transform === 'stripWhitespace' ? next.replace(/\s+/g, '') : next.toUpperCase();
  }
  if (maxLength && next.length > maxLength) next = next.substring(0, maxLength);
  return next;
}

/** Move the caret; swallow the DOMException input types without a selection throw. */
function setCaret(input: HTMLInputElement, position: number): void {
  try {
    input.setSelectionRange(position, position);
  } catch {
    /* input type does not support selection — leave the caret alone */
  }
}

/**
 * `(input)` handler: correct the value after each keystroke and keep the caret where
 * the user is working.
 *
 * Assigning `input.value` collapses the caret to the end of the field, so editing the
 * middle would throw the caret away. The caret is rebuilt by transforming only the
 * text BEFORE it: its transformed length is the new caret offset. Typing a space
 * (removed) leaves the caret in place; typing `a` (-> `A`) puts it just after the `A`.
 */
export function applyInputTransform(
  event: Event,
  transforms: readonly InputTransform[] | undefined,
  maxLength: number | undefined,
  qname: string,
  formGroup: FormGroup
): void {
  if (!transforms?.length) return;

  const input = event.target as HTMLInputElement;
  const rawValue = input.value;
  // Read BEFORE rewriting the value — assigning it resets the selection.
  const rawCaret = input.selectionStart;

  const value = applyInputTransforms(rawValue, transforms, maxLength);

  const control = formGroup.get(qname);
  if (!control) return;

  // emitEvent:false is the established pattern (see applyInputFilter): Angular still
  // re-runs the validators against the corrected value, so the inline error reflects
  // what the field now shows, never the raw keystroke.
  control.setValue(value, { emitEvent: false });
  if (input.value !== value) input.value = value;
  if (rawCaret !== null) {
    setCaret(input, applyInputTransforms(rawValue.substring(0, rawCaret), transforms, maxLength).length);
  }
}

/**
 * `(paste)` handler: insert the cleaned clipboard text AT THE SELECTION (replacing any
 * selected range), not over the whole field, and put the caret just after it.
 *
 * Intercepted with `preventDefault` because the browser applies the native `maxlength`
 * to pasted text BEFORE any handler runs: `AF 001800` would arrive truncated to
 * `AF 00180`, and stripping the space afterwards leaves a short, invalid value.
 */
export function onPasteInputTransform(
  event: ClipboardEvent,
  transforms: readonly InputTransform[] | undefined,
  maxLength: number | undefined,
  qname: string,
  formGroup: FormGroup
): void {
  if (!transforms?.length) return;

  event.preventDefault();

  const input = event.target as HTMLInputElement;
  const control = formGroup.get(qname);
  if (!control) return;

  const pasted = event.clipboardData?.getData('text') || '';
  const current = input.value;
  const start = input.selectionStart ?? current.length;
  const end = input.selectionEnd ?? start;

  const before = current.substring(0, start) + pasted;
  const value = applyInputTransforms(before + current.substring(end), transforms, maxLength);
  const caret = applyInputTransforms(before, transforms, maxLength).length;

  control.setValue(value);
  control.markAsTouched();
  // setValue -> writeValue rewrote the input and collapsed the caret; restore it.
  if (input.value !== value) input.value = value;
  setCaret(input, caret);
}

/**
 * Re-apply the schema-declared transforms to a live form group (seam 3).
 *
 * Without it the state seam would store `AF1621` while the form still held `af 1621`,
 * and `form.valid` — which gates Submit — would be computed from a value that is not
 * the one submitted. Writes with `emitEvent:false` so it cannot re-trigger the
 * `valueChanges` pipeline that called it; validators still re-run.
 *
 * @returns `true` if any control was rewritten.
 */
export function applyInputTransformsToForm(form: FormGroup): boolean {
  let changed = false;

  INPUT_TRANSFORMS_BY_QNAME.forEach((rule, qname) => {
    const control = form.get(qname);
    if (!control || typeof control.value !== 'string') return;

    const next = applyInputTransforms(control.value, rule.transforms, rule.maxLength);
    if (next === control.value) return;

    control.setValue(next, { emitEvent: false });
    changed = true;
  });

  return changed;
}
